import React, { useState } from 'react';
import styles from "./ContactUs.module.scss";
import { Autocomplete, TextField } from '@mui/material';
import Button from '@mui/material/Button';
import ProtectedPaymentRoute from '../../auth/Auth';
import InputMask from 'react-input-mask';
import { useNavigate } from 'react-router-dom';
import { CONTACT_US_URL } from '../../../services/const';

const messageTypes = [
  { label: 'Formation', value: 'formation' },
  { label: 'Compliance (BOI)', value: 'compliance' },
  { label: 'Licensing', value: 'licensing' },
  { label: 'Existing Order', value: 'existingOrder' },
  { label: 'Other', value: 'other' },
];

export default function ContactUs() {
  ProtectedPaymentRoute();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [subject, setSubject] = useState(null);
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});

  const renderError = (field) => {
    if (!errors[field]) return null;
    return <p className="text-[#d93025] text-[12px] font-medium mt-1 mb-0">{errors[field]}</p>;
  };

  const validateFields = () => {
    const newErrors = {};
    const alphaSpaceRegex = /^[A-Za-z\s]*$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!firstName || !alphaSpaceRegex.test(firstName)) {
      newErrors.firstName = 'Enter valid first name';
    }

    if (!lastName || !alphaSpaceRegex.test(lastName)) {
      newErrors.lastName = 'Enter valid last name';
    }

    if (!businessName || !alphaSpaceRegex.test(businessName)) {
      newErrors.businessName = 'Enter valid business name';
    }

    if (!email || !emailRegex.test(email)) {
      newErrors.email = 'Enter valid email address';
    }

    if (!phone || phone.replace(/[^0-9]/g, '').length !== 10) {
      newErrors.phone = 'Enter a 10-digit phone number';
    }

    if (!subject) {
      newErrors.subject = 'Select a subject';
    }

    if (!message.trim()) {
      newErrors.message = 'Enter a message';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (validateFields()) {
      const contactData = {
        firstName,
        lastName,
        email,
        phone,
        businessName,
        message,
        subject: subject.value
      };

      try {
        const response = await fetch(CONTACT_US_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(contactData)
        });

        if (response.ok) {
          navigate('/greeting');
        } else {
          console.error('Failed to send contact data');
        }
      } catch (error) {
        console.error('Error:', error);
      }
    }
  };

  return (
    <div className="font-sans antialiased bg-white text-[#202124]">
      
      {/* Hero Section */}
      <section 
        className="relative overflow-hidden pt-[140px] pb-20 lg:pt-[180px] lg:pb-28 border-b border-[#0f2c4f] flex items-center justify-center"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=2560&auto=format&fit=crop')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        {/* Professional Deep Blue Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#021835] via-[#04244d]/90 to-[#04244d]/40 mix-blend-multiply"></div>
        <div className="absolute inset-0 bg-[#021835]/50"></div>

        <div className="container mx-auto px-4 lg:px-8 relative z-10 text-center flex flex-col items-center justify-center">
          <span className="text-[#8ab4f8] font-medium tracking-wider uppercase text-sm mb-3 block">
            Dedicated Customer Support
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-[4.2rem] font-medium leading-[1.12] mb-6 tracking-tight text-white max-w-4xl">
            Get in Touch With Us
          </h1>
          <p className="text-lg md:text-[1.15rem] text-white/80 leading-[1.7] font-normal max-w-2xl mx-auto">
            Our team of business specialists and legal advisers are standing by to guide your enterprise at every step.
          </p>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-20 lg:py-24 bg-[#f8f9fa] border-b border-[#dadce0]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            
            {/* Left Info Cards */}
            <div className="lg:col-span-5 space-y-6">
              <div>
                <span className="text-[#1a73e8] font-medium tracking-wider uppercase text-xs mb-2 block">
                  Reach Out Directly
                </span>
                <h2 className="text-3xl font-medium text-[#202124] tracking-tight mb-4">
                  We're Here to Help
                </h2>
                <p className="text-[15px] text-[#5f6368] leading-relaxed mb-8">
                  Whether you have questions regarding state filing procedures, licensing requirements, or existing order statuses, our specialists are ready to assist.
                </p>
              </div>

              {/* Address Card */}
              <div className="bg-white p-6 rounded-[12px] border border-[#dadce0] shadow-sm flex items-start gap-4 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 rounded-full bg-[#e8f0fe] flex items-center justify-center text-[#1a73e8] flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
                </div>
                <div>
                  <h3 className="text-[17px] font-medium text-[#202124] mb-1">Corporate Address</h3>
                  <p className="text-[14.5px] text-[#5f6368] leading-relaxed m-0">5901 Chase Rd Suite 220, Dearborn, MI 48126</p>
                </div>
              </div>

              {/* Phone Card */}
              <div className="bg-white p-6 rounded-[12px] border border-[#dadce0] shadow-sm flex items-start gap-4 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 rounded-full bg-[#e8f0fe] flex items-center justify-center text-[#1a73e8] flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                </div>
                <div>
                  <h3 className="text-[17px] font-medium text-[#202124] mb-1">Phone Support</h3>
                  <p className="text-[14.5px] text-[#5f6368] leading-relaxed m-0 font-medium text-[#1a73e8]">855-OKTOBIZ (855-658-6249)</p>
                </div>
              </div>

              {/* Email Card */}
              <div className="bg-white p-6 rounded-[12px] border border-[#dadce0] shadow-sm flex items-start gap-4 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 rounded-full bg-[#e8f0fe] flex items-center justify-center text-[#1a73e8] flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                </div>
                <div>
                  <h3 className="text-[17px] font-medium text-[#202124] mb-1">Email Inquiries</h3>
                  <p className="text-[14.5px] text-[#5f6368] leading-relaxed m-0 font-medium text-[#1a73e8]">support@oktobiz.com</p>
                </div>
              </div>

            </div>

            {/* Right Contact Form Card */}
            <div className="lg:col-span-7 bg-white p-8 sm:p-10 lg:p-12 rounded-[16px] border border-[#dadce0] shadow-sm">
              <div className="mb-8">
                <h3 className="text-2xl font-medium text-[#202124] tracking-tight mb-2">Send Us a Message</h3>
                <p className="text-[15px] text-[#5f6368]">Fill in your information below and our team will get back to you promptly.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <TextField
                      variant="outlined"
                      fullWidth
                      id="firstName"
                      label="First Name"
                      name="firstName"
                      error={!!errors.firstName}
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                    {renderError("firstName")}
                  </div>
                  <div>
                    <TextField
                      variant="outlined"
                      fullWidth
                      id="lastName"
                      label="Last Name"
                      name="lastName"
                      value={lastName}
                      error={!!errors.lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                    {renderError("lastName")}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <TextField
                      variant="outlined"
                      fullWidth
                      id="email"
                      label="Email Address"
                      name="email"
                      value={email}
                      error={!!errors.email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    {renderError("email")}
                  </div>
                  <div>
                    <InputMask
                      mask="999-999-9999"
                      maskChar=""
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    >
                      {() => <TextField
                        variant="outlined"
                        fullWidth
                        id="phone"
                        label="Phone Number"
                        name="phone"
                        error={!!errors.phone}
                      />}
                    </InputMask>
                    {renderError("phone")}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <TextField
                      variant="outlined"
                      fullWidth
                      id="businessName"
                      label="Business Name"
                      name="businessName"
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                      error={!!errors.businessName}
                    />
                    {renderError("businessName")}
                  </div>
                  <div>
                    <Autocomplete
                      fullWidth
                      id="subject"
                      options={messageTypes}
                      getOptionLabel={(option) => option.label}
                      value={subject}
                      onChange={(e, newValue) => setSubject(newValue)}
                      renderInput={(params) => <TextField
                        {...params}
                        label="Inquiry Subject"
                        variant="outlined"
                        error={!!errors.subject}
                      />}
                    />
                    {renderError("subject")}
                  </div>
                </div>

                <div>
                  <TextField
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={4}
                    id="message"
                    label="How can we help you?"
                    name="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    error={!!errors.message}
                  />
                  {renderError("message")}
                </div>

                <button 
                  type="submit" 
                  className="w-full bg-[#1a73e8] hover:bg-[#1557b0] !text-white py-3.5 px-8 rounded-[4px] font-medium text-[15px] shadow-sm transition-colors cursor-pointer"
                >
                  Submit Inquiry
                </button>
              </form>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
